/**
 * Ad Matching Algorithm for VindJouwBandLid
 * 
 * Calculates match scores between a user's profile and available ads.
 * Higher score = better match.
 */

// Province proximity map (adjacent provinces get partial score)
const PROVINCE_NEIGHBORS = {
  'NH': ['NH', 'UT', 'FL', 'ZH'],           // Noord-Holland
  'ZH': ['ZH', 'NH', 'UT', 'NB', 'ZE'],     // Zuid-Holland
  'UT': ['UT', 'NH', 'ZH', 'GE', 'NB', 'FL'], // Utrecht
  'NB': ['NB', 'ZH', 'UT', 'GE', 'LI', 'ZE'], // Noord-Brabant
  'GE': ['GE', 'UT', 'NB', 'LI', 'OV', 'FL'], // Gelderland
  'LI': ['LI', 'NB', 'GE'],                 // Limburg
  'OV': ['OV', 'GE', 'FL', 'DR'],           // Overijssel
  'FL': ['FL', 'NH', 'UT', 'GE', 'OV'],     // Flevoland
  'DR': ['DR', 'OV', 'FR', 'GR'],           // Drenthe
  'FR': ['FR', 'DR', 'GR'],                 // Friesland
  'GR': ['GR', 'DR', 'FR'],                 // Groningen
  'ZE': ['ZE', 'ZH', 'NB'],                 // Zeeland
  // Belgian provinces
  'ANT': ['ANT', 'VLB', 'LIM', 'OVL'],      // Antwerpen
  'VLB': ['VLB', 'ANT', 'BRU', 'WBR', 'LIM'], // Vlaams-Brabant
  'OVL': ['OVL', 'ANT', 'WVL', 'HAI'],      // Oost-Vlaanderen
  'WVL': ['WVL', 'OVL'],                    // West-Vlaanderen
  'LIM_BE': ['LIM_BE', 'ANT', 'VLB', 'LIE'], // Limburg (BE)
  'BRU': ['BRU', 'VLB', 'WBR'],             // Brussel
  'WBR': ['WBR', 'VLB', 'BRU', 'HAI', 'NAM'], // Waals-Brabant
  'HAI': ['HAI', 'OVL', 'WBR', 'NAM'],      // Henegouwen
  'NAM': ['NAM', 'WBR', 'HAI', 'LIE', 'LUX'], // Namen
  'LIE': ['LIE', 'LIM_BE', 'NAM', 'LUX'],   // Luik
  'LUX': ['LUX', 'NAM', 'LIE'],             // Luxemburg
};

// Experience level compatibility (some flexibility allowed)
const EXPERIENCE_COMPATIBILITY = {
  'beginner': ['beginner', 'intermediate'],
  'intermediate': ['beginner', 'intermediate', 'advanced'],
  'advanced': ['intermediate', 'advanced', 'professional'],
  'professional': ['advanced', 'professional'],
};

/**
 * Calculate match score between a user profile and an ad
 * @param {Object} profile - User's profile
 * @param {Object} ad - The ad to match against
 * @returns {Object} { score: number, reasons: string[] }
 */
export function calculateMatchScore(profile, ad) {
  if (!profile || !ad) return { score: 0, reasons: [] };
  
  let score = 0;
  const reasons = [];
  
  // 1. Instrument match (most important - 40 points max)
  // Does the ad look for instruments the user plays?
  const userInstruments = profile.instruments || [];
  const adLookingFor = ad.lookingFor || [];
  
  const instrumentMatches = userInstruments.filter(inst => 
    adLookingFor.includes(inst)
  );
  
  if (instrumentMatches.length > 0) {
    // More matching instruments = higher score
    const instrumentScore = Math.min(40, instrumentMatches.length * 20);
    score += instrumentScore;
    reasons.push(`Zoekt ${instrumentMatches.join(', ')}`);
  }
  
  // 2. Genre overlap (30 points max)
  const userGenres = profile.genres || [];
  const adGenres = ad.genres || [];
  
  const genreMatches = userGenres.filter(g => adGenres.includes(g));
  
  if (genreMatches.length > 0) {
    // Up to 30 points for genre overlap
    const genreScore = Math.min(30, genreMatches.length * 10);
    score += genreScore;
    if (genreMatches.length >= 2) {
      reasons.push(`${genreMatches.length} genres overeen`);
    }
  }
  
  // 3. Location proximity (20 points max)
  const userProvince = profile.location?.province;
  const adProvince = ad.location?.province;
  
  if (userProvince && adProvince) {
    if (userProvince === adProvince) {
      // Same province
      score += 20;
      reasons.push('Zelfde provincie');
    } else if (PROVINCE_NEIGHBORS[userProvince]?.includes(adProvince)) {
      // Neighboring province
      score += 10;
      reasons.push('In de buurt');
    }
  }
  
  // 4. Experience level compatibility (10 points)
  const userExp = profile.experienceLevel;
  const adExp = ad.experienceLevel;
  
  if (userExp && adExp) {
    if (userExp === adExp) {
      score += 10;
    } else if (EXPERIENCE_COMPATIBILITY[userExp]?.includes(adExp)) {
      score += 5;
    }
  } else if (!adExp) {
    // Ad has no preference, slight bonus
    score += 5;
  }
  
  // 5. Goals alignment bonus (bonus points)
  // Matching goals gets a small bonus
  if (profile.goals && profile.goals === ad.goals) {
    score += 5;
  }
  
  // 6. Recency bonus (up to 5 points)
  // Newer ads get a small boost
  const adAge = Date.now() - new Date(ad.createdAt).getTime();
  const dayInMs = 24 * 60 * 60 * 1000;
  
  if (adAge < dayInMs) {
    score += 5; // Posted today
    reasons.push('Nieuw');
  } else if (adAge < 3 * dayInMs) {
    score += 3; // Posted in last 3 days
  } else if (adAge < 7 * dayInMs) {
    score += 1; // Posted this week
  }
  
  return { score, reasons };
}

/**
 * Get recommended ads for a user, sorted by match score
 * @param {Object} profile - User's profile
 * @param {Array} allAds - All available ads
 * @param {string} userId - Current user's ID (to exclude own ads)
 * @param {number} limit - Max number of results
 * @returns {Array} Sorted array of { ad, score, reasons }
 */
export function getRecommendedAds(profile, allAds, userId, limit = 5) {
  if (!profile || !allAds || allAds.length === 0) {
    return [];
  }
  
  // Filter and score ads
  const scoredAds = allAds
    .filter(ad => {
      // Must be active
      if (ad.status !== 'active') return false;
      // Can't be own ad
      if (ad.createdBy === userId) return false;
      return true;
    })
    .map(ad => {
      const { score, reasons } = calculateMatchScore(profile, ad);
      return { ad, score, reasons };
    })
    // Sort by score descending
    .sort((a, b) => b.score - a.score)
    // Take top results
    .slice(0, limit);
  
  return scoredAds;
}

/**
 * Check if an ad is a good match (score threshold)
 * @param {number} score - Match score
 * @returns {string} 'excellent' | 'good' | 'fair' | 'low'
 */
export function getMatchQuality(score) {
  if (score >= 60) return 'excellent';
  if (score >= 40) return 'good';
  if (score >= 20) return 'fair';
  return 'low';
}

/**
 * Get match quality label in Dutch
 * @param {string} quality - Match quality
 * @returns {string} Dutch label
 */
export function getMatchLabel(quality) {
  const labels = {
    'excellent': 'Uitstekende match',
    'good': 'Goede match',
    'fair': 'Redelijke match',
    'low': 'Mogelijk interessant',
  };
  return labels[quality] || '';
}
